<template>
  <div class="talent-plan-page">
    <div class="talent-plan-container">
      <!-- 国家级人才计划解读 -->
      <section class="plan-section" data-aos="fade-up">
        <h2 class="section-title">{{ $t('talentPlan.sections.national') }}</h2>
        <div class="plan-list" data-aos="fade-up" data-aos-delay="200">
          <div v-for="(item, index) in currentNationalPlans" 
               :key="item.id" 
               class="plan-item" 
               @click="goToDetail(item)"
               :data-aos="'fade-up'"
               :data-aos-delay="100 * (index + 1)"
          >
            <div class="plan-item-container">
              <div class="plan-image">
                <img :src="item.url" :alt="item.title">
              </div>
              <h3 class="plan-title">{{ item.title }}</h3>
              <p class="plan-desc" v-html="filterContent(item.content)"></p>
            </div>
          </div>
        </div>
        <!-- 分页器 -->
        <div class="pagination">
          <button class="prev-btn" @click="prevPage('national')" :disabled="nationalCurrentPage === 1">
            &lt;
          </button>
          <div class="page-numbers">
            <span v-for="page in nationalTotalPages" 
                  :key="page" 
                  :class="['page-number', { active: page === nationalCurrentPage }]"
                  @click="goToPage('national', page)">
              {{ page }}
            </span>
          </div>
          <button class="next-btn" @click="nextPage('national')" :disabled="nationalCurrentPage === nationalTotalPages">
            &gt;
          </button>
        </div>
      </section>

      <!-- 省级人才计划解读 -->
      <section class="plan-section" data-aos="fade-up">
        <h2 class="section-title">{{ $t('talentPlan.sections.provincial') }}</h2>
        <div class="plan-list" data-aos="fade-up" data-aos-delay="200">
          <div v-for="(item, index) in currentProvincialPlans" 
               :key="item.id" 
               class="plan-item" 
               @click="goToDetail(item)"
               :data-aos="'fade-up'"
               :data-aos-delay="100 * (index + 1)"
          >
            <div class="plan-item-container">
              <div class="plan-image">
                <img :src="item.url" :alt="item.title">
              </div>
            <h3 class="plan-title">{{ item.title }}</h3>
              <p class="plan-desc" v-html="filterContent(item.content)"></p>
            </div>
          </div>
        </div>
        <!-- 分页器 -->
        <div class="pagination">
          <button class="prev-btn" @click="prevPage('provincial')" :disabled="provincialCurrentPage === 1">
            &lt;
          </button>
          <div class="page-numbers">
            <span v-for="page in provincialTotalPages" 
                  :key="page" 
                  :class="['page-number', { active: page === provincialCurrentPage }]"
                  @click="goToPage('provincial', page)">
              {{ page }}
            </span>
          </div>
          <button class="next-btn" @click="nextPage('provincial')" :disabled="provincialCurrentPage === provincialTotalPages">
            &gt;
          </button>
        </div>
      </section>

      <!-- 地市级人才计划解读 -->
      <section class="plan-section" data-aos="fade-up">
        <h2 class="section-title">{{ $t('talentPlan.sections.city') }}</h2>
        <div class="plan-list" data-aos="fade-up" data-aos-delay="200">
          <div v-for="(item, index) in currentCityPlans" 
               :key="item.id" 
               class="plan-item" 
               @click="goToDetail(item)"
               :data-aos="'fade-up'"
               :data-aos-delay="100 * (index + 1)"
          >
            <div class="plan-item-container">
              <div class="plan-image">
                <img :src="item.url" :alt="item.title">
              </div>
            <h3 class="plan-title">{{ item.title }}</h3>
              <p class="plan-desc" v-html="filterContent(item.content)"></p>
            </div>
          </div>
        </div>
        <!-- 分页器 -->
        <div class="pagination">
          <button class="prev-btn" @click="prevPage('city')" :disabled="cityCurrentPage === 1">
            &lt;
          </button>
          <div class="page-numbers">
            <span v-for="page in cityTotalPages" 
                  :key="page" 
                  :class="['page-number', { active: page === cityCurrentPage }]"
                  @click="goToPage('city', page)">
              {{ page }}
            </span>
          </div>
          <button class="next-btn" @click="nextPage('city')" :disabled="cityCurrentPage === cityTotalPages">
            &gt;
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getTalentPlanInfo } from '@/api/talent'
import AOS from 'aos'
import 'aos/dist/aos.css'

export default {
  name: 'TalentPlan',
  data() {
    return {
      itemsPerPage: 8,
      nationalPlans: [],
      provincialPlans: [],
      cityPlans: [],
      nationalCurrentPage: 1,
      provincialCurrentPage: 1,
      cityCurrentPage: 1,
      nationalTotal: 0,
      provincialTotal: 0,
      cityTotal: 0
    }
  },
  computed: {
    // 国家级当前页数据
    currentNationalPlans() {
      return this.nationalPlans
    },
    // 省级当前页数据
    currentProvincialPlans() {
      return this.provincialPlans
    },
    // 地市级当前页数据
    currentCityPlans() {
      return this.cityPlans
    },
    // 计算总页数
    nationalTotalPages() {
      return Math.ceil(this.nationalTotal / this.itemsPerPage)
    },
    provincialTotalPages() {
      return Math.ceil(this.provincialTotal / this.itemsPerPage)
    },
    cityTotalPages() {
      return Math.ceil(this.cityTotal / this.itemsPerPage)
    }
  },
  created() {
    this.getAllPlanInfo()
  },
  mounted() {
    // 初始化 AOS
    AOS.init({
      duration: 1000,    // 动画持续时间
      once: true,        // 动画是否只执行一次
      offset: 100,       // 触发动画的位置
      easing: 'ease-in-out-cubic' // 动画曲线
    })
  },
  methods: {
    // 获取所有级别的数据
    async getAllPlanInfo() {
      await Promise.all([
        this.getPlanInfo('national'),
        this.getPlanInfo('provincial'),
        this.getPlanInfo('city')
      ])
    },
    // 获取指定级别的数据
    async getPlanInfo(type) {
      try {
        const levelMap = {
          national: 1,
          provincial: 2,
          city: 3
        }
        
        const pageMap = {
          national: this.nationalCurrentPage,
          provincial: this.provincialCurrentPage,
          city: this.cityCurrentPage
        }
        
        const res = await getTalentPlanInfo({
          page: pageMap[type],
          pageSize: this.itemsPerPage,
          level: levelMap[type]
        })
        
        // 更新对应的数据和总数
        switch(type) {
          case 'national':
            this.nationalPlans = res.data.data || []
            this.nationalTotal = res.data.total || 0
            break
          case 'provincial':
            console.log('省级人才计划解读信息', res.data)
            this.provincialPlans = res.data.data || []
            console.log('省级人才计划解读数据', this.provincialPlans)
            this.provincialTotal = res.data.total || 0
            break
          case 'city':
            this.cityPlans = res.data.data || []
            this.cityTotal = res.data.total || 0
            break
        }
      } catch (error) {
        console.error(`获取${type}人才计划解读信息失败:`, error)
      }
    },
    // 上一页
    async prevPage(type) {
      switch(type) {
        case 'national':
          if (this.nationalCurrentPage > 1) {
            this.nationalCurrentPage--
            await this.getPlanInfo('national')
          }
          break
        case 'provincial':
          if (this.provincialCurrentPage > 1) {
            this.provincialCurrentPage--
            await this.getPlanInfo('provincial')
          }
          break
        case 'city':
          if (this.cityCurrentPage > 1) {
            this.cityCurrentPage--
            await this.getPlanInfo('city')
          }
          break
      }
    },
    // 下一页
    async nextPage(type) {
      switch(type) {
        case 'national':
          if (this.nationalCurrentPage < this.nationalTotalPages) {
            this.nationalCurrentPage++
            await this.getPlanInfo('national')
          }
          break
        case 'provincial':
          if (this.provincialCurrentPage < this.provincialTotalPages) {
            this.provincialCurrentPage++
            await this.getPlanInfo('provincial')
          }
          break
        case 'city':
          if (this.cityCurrentPage < this.cityTotalPages) {
            this.cityCurrentPage++
            await this.getPlanInfo('city')
          }
          break
      }
    },
    // 跳转到指定页
    async goToPage(type, page) {
      switch(type) {
        case 'national':
          this.nationalCurrentPage = page
          await this.getPlanInfo('national')
          break
        case 'provincial':
          this.provincialCurrentPage = page
          await this.getPlanInfo('provincial')
          break
        case 'city':
          this.cityCurrentPage = page
          await this.getPlanInfo('city')
          break
      }
    },
    // 添加富文本内容过滤方法
    filterContent(content) {
      if (!content) return ''
      
      // 创建一个临时的 div 元素来解析富文本
      const temp = document.createElement('div')
      temp.innerHTML = content
      
      // 移除所有图片标签
      const imgs = temp.getElementsByTagName('img')
      while (imgs.length > 0) {
        imgs[0].parentNode.removeChild(imgs[0])
      }
      
      // 移除所有视频标签
      const videos = temp.getElementsByTagName('video')
      while (videos.length > 0) {
        videos[0].parentNode.removeChild(videos[0])
      }
      
      // 移除所有音频��签
      const audios = temp.getElementsByTagName('audio')
      while (audios.length > 0) {
        audios[0].parentNode.removeChild(audios[0])
      }
      
      // 移除所有 iframe 标签
      const iframes = temp.getElementsByTagName('iframe')
      while (iframes.length > 0) {
        iframes[0].parentNode.removeChild(iframes[0])
      }
      
      // 获取纯文本内容
      let text = temp.textContent || temp.innerText
      
      // 去除多余空格和换行
      text = text.replace(/\s+/g, ' ').trim()
      
      return text
    },
    goToDetail(item) {
           // 页面内跳转
      // console.log('item', item)
      // // 将数据转换为字符串并通过路由参数传递
      // this.$router.push({
      //   // name: 'TalentPlanDetail',
      //   path: `/talent/plan/detail/${item.id}`,
      //   query: {
      //     level: item.level
      //   },
      //   params: {
      //     detailData: JSON.stringify(item)
      //   }
      // })

      // 下面的是跳转到新页面
      console.log('item', item);

      // 将数据存储到 localStorage
      localStorage.setItem('detailData', JSON.stringify(item));

        // 获取当前语言
      const currentLanguage = this.$i18n.locale;

      // 使用 router.resolve 生成完整的 URL
      const resolved = this.$router.resolve({
        path: `/talent/plan/detail/${item.id}`,
        query: {
          level: item.level,
          lang: currentLanguage // 添加语言参数
        }
      });

      // 在新标签页中打开
      window.open(resolved.href, '_blank');
    }
  }
}
</script>

<style scoped>
.talent-plan-page {
  min-height: 100vh;
  background: #F6F7F9;
  padding-top: 80px;
  padding-left: 5%;
  padding-right: 5%;
}

.talent-plan-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 0;
}

.plan-section {
  border-radius: 12px;
  padding: 40px 0;
  margin-bottom: 40px;
  transition: all 0.3s ease-in-out;
}

.section-title {
  font-family: PingFangSC, PingFang SC;
  font-weight: 600;
  font-size: 30px;
  color: #484848;
  line-height: 42px;
  margin-bottom: 40px;
  text-align: center;
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: #3379F2;
  transition: width 0.3s ease-in-out;
}

.plan-section:hover .section-title::after {
  width: 100%;
}

.plan-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

/* .plan-item {
  
} */


.plan-item-container{
  background: #FFFFFF;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  height: 100%;
}


/* .plan-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
} */

.plan-item-container:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.plan-image {
  width: 100%;
  height: 160px;
  padding-bottom: 56.25%; /* 16:9 比例 */
  position: relative;
  overflow: hidden;
}

.plan-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* object-fit: cover; */
  transition: all 0.3s ease-in-out;
}

.plan-item:hover .plan-image img {
  transform: scale(1.05);
}

.plan-title {
  font-size: 16px;
  font-weight: 600;
  color: #333333;
  margin: 10px 15px;
  line-height: 1.4;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
  text-overflow: ellipsis;
}

.plan-desc {
  margin: 0 15px 15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
  text-overflow: ellipsis;
  font-family: PingFangSC, PingFang SC;
font-weight: 400;
font-size: 14px;
color: #888888;
line-height: 20px;
text-align: left;
font-style: normal;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 30px;
}

.prev-btn,
.next-btn {
  width: 32px;
  height: 32px;
  background: #FFFFFF;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #606266;
  font-size: 16px; /* 调整箭头大小 */
  margin: 0 4px;
  padding: 0;
  line-height: 1;
  transition: all 0.3s ease-in-out;
}

.prev-btn:hover,
.next-btn:hover {
  color: #3379F2;
  border-color: #3379F2;
  transform: translateY(-2px);
}

.prev-btn:disabled,
.next-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.page-numbers {
  display: flex;
  gap: 10px;
}

.page-number {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  color: #606266;
  font-size: 14px;
  background: #FFFFFF;
border-radius: 2px;
  transition: all 0.3s ease-in-out;
}

.page-number.active {
  background: #3379F2;
  color: #FFFFFF;
}

.page-number:hover:not(.active) {
  transform: translateY(-2px);
  color: #3379F2;
}

@media screen and (max-width: 1200px) {
  .plan-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .plan-list {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .plan-section {
    padding: 20px;
  }
  
  .section-title {
    font-size: 24px;
    margin-bottom: 30px;
  }
}

@media screen and (max-width: 480px) {
  .plan-list {
    grid-template-columns: 1fr;
  }
}
</style> 